import {
  Billing,
  objectIdSchema,
  paginationWithSearchSchema,
  businessSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/** GET /billing — single Billing by id (caller must own it or be admin). */
export const billingGetOneAPISchema = z.object({
  _id: objectIdSchema.describe('`Billing._id` (24-char Mongo ObjectId).'),
});
export type BillingGetOneAPI = API<z.infer<typeof billingGetOneAPISchema>, Billing>;

/** GET /billing/many — my org's billings, paginated. */
export const billingGetManyAPISchema = z
  .object({
    status: z
      .enum(['draft', 'pending', 'partially_paid', 'paid', 'past_due', 'cancelled', 'refunded'])
      .optional()
      .describe(
        "Billing lifecycle status filter. 'past_due' = pending but past due date. 'partially_paid' = some payment received, balance remains.",
      ),
    source: z
      .enum([
        'platform_subscription',
        'platform_addon_recurring',
        'platform_addon_one_time',
        'platform_manual',
      ])
      .optional()
      .describe(
        "Source of the Billing line: subscription / recurring addon / one-time addon / manual admin entry.",
      ),
    business: businessSchema
      .optional()
      .describe(
        'Brand that earned the money — `hero` or `wathiqa_plus`. Filters on `purchase.business`. Billings issued before purchase was stamped match neither value.',
      ),
    payerId: objectIdSchema
      .optional()
      .describe(
        'Admin-only — filter to one payer (`Billing.payer._id`). Named `payerId` and not `orgId` because a freelancer bills as themselves — a `User` id, not an Org one — and the old name made every owner-operator invoice look unfilterable.',
      ),
    leadId: objectIdSchema
      .optional()
      .describe(
        'Admin-only — filter to one lead (`Billing.leadId`, stamped at draft time). Ignored when `payerId` is set. Only Billings created after the field was introduced carry it.',
      ),
  })
  .extend(paginationWithSearchSchema.shape);
export type BillingGetManyAPI = API<z.infer<typeof billingGetManyAPISchema>, Billing[]>;

/** GET /billing/admin/all — admin paginated list across all orgs. */
export const billingAdminGetAllAPISchema = billingGetManyAPISchema;
export type BillingAdminGetAllAPI = API<
  z.infer<typeof billingAdminGetAllAPISchema>,
  Billing[]
>;

/** POST /billing/admin/cancel — admin cancels a draft / pending Billing. */
export const billingAdminCancelAPISchema = z.object({
  _id: objectIdSchema,
  reason: z.string().max(500).optional(),
});
export type BillingAdminCancelAPI = API<
  z.infer<typeof billingAdminCancelAPISchema>,
  Billing
>;
